import type {
  ElectronicContractCustomerType,
  ElectronicContractLifecycle,
  ElectronicContractTemplateModel,
} from "../models/electronic-contract.model";

export const documentTypeLabels: Record<string, string> = {
  CONNECTION_REQUEST: "01 - Đề nghị cấp nước",
  INSTALLATION_ACCEPTANCE: "02 - Nghiệm thu lắp đặt",
  INSTALLATION_SURVEY: "03 - Khảo sát thỏa thuận",
  METER_HANDOVER: "04 - Bàn giao sử dụng",
  WATER_SERVICE_CONTRACT: "05 - Hợp đồng cấp nước",
  SERVICE_TERMINATION: "06 - Thanh lý hợp đồng",
};

export function getTemplateVariant(
  template: ElectronicContractTemplateModel
): { documentType: string; customerType: ElectronicContractCustomerType } | null {
  if (!template.matBaoCAConfig) return null;
  try {
    const config: unknown = JSON.parse(template.matBaoCAConfig);
    if (!config || typeof config !== "object" || Array.isArray(config)) return null;
    const values = config as Record<string, unknown>;
    const version = values.ApiVersion ?? values.apiVersion;
    const documentType = values.DocumentType ?? values.documentType;
    const customerType = values.CustomerType ?? values.customerType;
    if (version !== 2 || typeof documentType !== "string" ||
        !Object.prototype.hasOwnProperty.call(documentTypeLabels, documentType) ||
        (customerType !== "CN" && customerType !== "DN")) return null;
    return { documentType, customerType };
  } catch {
    return null;
  }
}

export function canCompanySign(
  lifecycle: ElectronicContractLifecycle | null | undefined,
  documentType?: string
): boolean {
  if (!lifecycle || lifecycle.requiresReconciliation !== false || lifecycle.providerStatus !== "PROCESSING" ||
      lifecycle.status !== "Sent" ||
      ![undefined, null, "", "Rejected"].includes(lifecycle.companySignState)) return false;
  if (documentType && !Object.prototype.hasOwnProperty.call(documentTypeLabels, documentType)) return false;
  const survey = documentType === "INSTALLATION_SURVEY";
  const companyIndex = survey ? 3 : 1;
  const expectedIndices = survey ? [1, 2, 3] : [1, 2];
  const signers = lifecycle.signers;
  if (!Array.isArray(signers) || signers.length !== expectedIndices.length ||
      expectedIndices.some(index => signers.filter(signer => signer?.signerIndex === index).length !== 1)) return false;
  const current = signers.filter(signer => signer.status === "PROCESSING");
  if (current.length !== 1 || current[0].signerIndex !== companyIndex || current[0].signerType !== "USER") return false;
  const customer = signers.find(signer => signer.signerIndex === (survey ? 1 : 2));
  if (!customer || !["PERSON", "COMPANY"].includes(customer.signerType ?? "")) return false;
  if (survey && signers.find(signer => signer.signerIndex === 2)?.signerType !== "USER") return false;
  // The public response preserves provider order, which is not guaranteed to be
  // signOrder. Require every other expected role to be DONE before company signing.
  return signers.every(signer => signer.signerIndex === companyIndex || signer.status === "DONE");
}

export function createContractRequestKey(): string {
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error("Trình duyệt không hỗ trợ tạo mã yêu cầu an toàn.");
  }
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
